import { Chip, CircularProgress, FormControlLabel, Stack, Switch, Tooltip } from '@mui/material';
import { type FeatureFlagKey } from '@nohara-dxdev/mt-common';
import { permission } from '@nohara-dxdev/oidc-core';
import { useCanAccess, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useTranslation } from 'react-i18next';

import { useToggleUserFeatureFlag } from '../api/toggle-user-feature-flag';
import { type Effective } from '../hooks/use-user-feature-flag-admin-state';

export interface FeatureFlagUserToggleProps {
  flagKey: FeatureFlagKey;
  userId: string;
  orgEnabled: boolean;
  userEnabled: boolean;
  effective: Effective;
}

export const FeatureFlagUserToggle = ({
  flagKey,
  userId,
  orgEnabled,
  userEnabled,
  effective,
}: FeatureFlagUserToggleProps) => {
  const { t } = useTranslation();
  const subject = useScopeIds();
  const toggle = useToggleUserFeatureFlag();

  const { canAccess: canEnable } = useCanAccess({
    requireAll: [permission.aiAgent.enable],
    context: subject,
  });
  const { canAccess: canDisable } = useCanAccess({
    requireAll: [permission.aiAgent.disable],
    context: subject,
  });

  let switchDisabled = false;
  let tooltipKey = '';
  if (!orgEnabled) {
    switchDisabled = true;
    tooltipKey = 'featureFlags.tooltips.orgMasterOff';
  } else if (userEnabled && !canDisable) {
    switchDisabled = true;
    tooltipKey = 'featureFlags.tooltips.cannotDisable';
  } else if (!userEnabled && !canEnable) {
    switchDisabled = true;
    tooltipKey = 'featureFlags.tooltips.cannotEnable';
  }

  const chipColor: 'success' | 'default' = effective === 'on' ? 'success' : 'default';
  const chipLabelKey =
    effective === 'on'
      ? 'featureFlags.effective.on'
      : effective === 'off-org'
        ? 'featureFlags.effective.offOrg'
        : 'featureFlags.effective.offUser';

  const isPending = toggle.isPending;

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Tooltip title={switchDisabled ? t(tooltipKey) : ''}>
        <span>
          <FormControlLabel
            control={
              <Switch
                checked={userEnabled}
                disabled={switchDisabled || isPending}
                onChange={(_, next) => {
                  toggle.mutate({ flagKey, userId, enabled: next });
                }}
                slotProps={{
                  input: {
                    role: 'switch',
                    'aria-label': t(`featureFlags.flags.${flagKey}.label`),
                  },
                }}
              />
            }
            label={t(`featureFlags.flags.${flagKey}.label`)}
          />
        </span>
      </Tooltip>
      {isPending && <CircularProgress size={16} aria-label={t('featureFlags.toggle.pending')} />}
      <Tooltip title={t(`featureFlags.effective.tooltip.${effective}`)}>
        <Chip color={chipColor} label={t(chipLabelKey)} size="small" />
      </Tooltip>
    </Stack>
  );
};
